import * as React from 'react'
// libs
import {
  AppendMessage,
  AssistantRuntimeProvider,
  AttachmentAdapter,
  CompleteAttachment,
  PendingAttachment,
  ThreadMessageLike,
  useExternalStoreRuntime
} from '@assistant-ui/react'
import { useMutation, useQuery } from '@apollo/client/react'
// hooks
import { useFileUpload } from 'hooks/useFileUpload'
import { useConversations } from './ConversationsContext'
// types
import { ChatMessage } from './types'
import { AssetType, IAiMessage, IAiRecordReference, IAiRiskSuggestion } from 'types/graphql'
// constants
import { ALLOWED_MIME_TYPES } from 'utils/validators'
// graphql
import { AI_MESSAGES_QUERY } from 'graphql/ai/queries'
import { SEND_AI_MESSAGE_MUTATION, SUMMARISE_RECORD_MUTATION } from 'graphql/ai/mutations'
// utils
import { v4 as uuid } from 'uuid'
import { getContextVariables } from './utils'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

export type MessageMetadata = {
  records: Record<string, IAiRecordReference[]>
  riskSuggestions: Record<string, IAiRiskSuggestion[]>
}

export const MessageMetadataContext = React.createContext<MessageMetadata>({ records: {}, riskSuggestions: {} })

// `summarize` is null for chats that aren't scoped to a single record
export const ChatActionsContext = React.createContext<{
  summarise: (() => Promise<void>) | null
  isSummarising: boolean
  isLoadingMessages: boolean
}>({ summarise: null, isSummarising: false, isLoadingMessages: false })

type UploadFn = (file: File) => Promise<string>

type AiMessagesResponse = {
  aiMessages: {
    data: IAiMessage[]
  }
}

type SummariseRecordResponse = {
  summariseRecord: {
    conversationId: string
    summary: string
  }
}

type SendAiMessagesResponse = {
  sendAiMessage: {
    response: string
    conversationId: string
    records: IAiRecordReference[]
    riskSuggestions: IAiRiskSuggestion[]
  }
}

class ChatAttachmentAdapter implements AttachmentAdapter {
  accept = ALLOWED_MIME_TYPES.join(',')

  private urls = new Map<string, string>()

  constructor(private uploadFn: UploadFn) {}

  async add({ file }: { file: File }): Promise<PendingAttachment> {
    const id = crypto.randomUUID()
    const url = await this.uploadFn(file)

    this.urls.set(id, url)

    return {
      id,
      file,
      type: file.type.startsWith('image/') ? 'image' : 'document',
      name: file.name,
      status: { type: 'requires-action', reason: 'composer-send' }
    }
  }

  async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
    const url = this.urls.get(attachment.id) ?? attachment.name

    this.urls.delete(attachment.id)

    return {
      id: attachment.id,
      type: attachment.type,
      name: attachment.name,
      content: [{ type: 'text', text: url }],
      status: { type: 'complete' }
    }
  }

  async remove(attachment: PendingAttachment): Promise<void> {
    this.urls.delete(attachment.id)
  }
}

const buildMetadata = (messages: ChatMessage[]): MessageMetadata => {
  const records: MessageMetadata['records'] = {}
  const riskSuggestions: MessageMetadata['riskSuggestions'] = {}

  messages.forEach((message) => {
    if (message.records?.length) {
      records[message.id] = message.records as IAiRecordReference[]
    }
    if (message.riskSuggestions?.length) {
      riskSuggestions[message.id] = message.riskSuggestions as IAiRiskSuggestion[]
    }
  })

  return { records, riskSuggestions }
}

const convertMessage = (message: ChatMessage): ThreadMessageLike => ({
  id: message.id,
  role: message.role,
  content: message.content ? [{ type: 'text', text: message.content }] : [],
  attachments: (message.attachments ?? []).map((att) => ({
    id: att.id,
    type: att.type,
    name: att.name,
    content: [{ type: 'text', text: att.url ?? att.name }],
    status: { type: 'complete' }
  })) as CompleteAttachment[]
})

const ChatRuntimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentConversationId, context, refetchConversations, setCurrentConversationId } = useConversations()

  const { data: messagesData, loading: isLoadingMessages } = useQuery<AiMessagesResponse>(AI_MESSAGES_QUERY, {
    variables: { conversationId: currentConversationId, pagination: { pageTake: 200, pageSkip: 0 } },
    skip: !currentConversationId,
    fetchPolicy: 'network-only'
  })

  const [sendAiMessage] = useMutation<SendAiMessagesResponse>(SEND_AI_MESSAGE_MUTATION)
  const [summariseRecord] = useMutation<SummariseRecordResponse>(SUMMARISE_RECORD_MUTATION)

  const conversationMessages: ChatMessage[] = React.useMemo(
    () =>
      (messagesData?.aiMessages?.data ?? []).map((message) => ({
        id: message.id,
        role: message.role as 'user' | 'assistant',
        content: message.content,
        records: message.records,
        riskSuggestions: message.riskSuggestions
      })),
    [messagesData]
  )

  const [messages, setMessages] = React.useState<ChatMessage[]>(conversationMessages)
  const [isRunning, setIsRunning] = React.useState(false)
  const [isSummarising, setIsSummarising] = React.useState(false)
  const [messageMetadata, setMessageMetadata] = React.useState<MessageMetadata>({ records: {}, riskSuggestions: {} })

  React.useEffect(() => {
    setMessageMetadata(buildMetadata(conversationMessages))
    setMessages(conversationMessages)
  }, [conversationMessages])

  const isMounted = React.useRef(true)
  const abortControllerRef = React.useRef<AbortController | null>(null)

  React.useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  const { upload } = useFileUpload()

  const uploadFn = React.useCallback(
    (file: File): Promise<string> =>
      upload(file, {
        file: {
          assetType: AssetType.ActivityFile,
          contentType: file.type,
          entityPrefix: 'ai/chat',
          originalFileName: file.name
        }
      }),
    [upload]
  )

  const attachmentAdapter = React.useMemo(() => new ChatAttachmentAdapter(uploadFn), [uploadFn])

  const riskId = context.type === 'risk' ? context.riskId : null

  // Triggered from the thread's Summarize button — asks the backend for a summary of the
  // record, which it stores as the first message of a (possibly new) conversation.
  // `isSummarising` replaces the greeting with a loader while the request is in flight.
  const summarise = React.useCallback(async () => {
    setIsRunning(true)
    setIsSummarising(true)

    try {
      const { data } = await summariseRecord({
        variables: { input: { riskId } }
      })

      if (!isMounted.current) {
        return
      }

      const result = data?.summariseRecord

      setMessages([{ id: uuid(), role: 'assistant', content: result?.summary ?? '' }])

      if (result?.conversationId) {
        setCurrentConversationId(result.conversationId)
        refetchConversations()
      }
    } catch (err) {
      console.error('[ChatRuntimeProvider.summarise]: ', parseGraphQLErrorMessages(err))
    } finally {
      if (isMounted.current) {
        setIsRunning(false)
        setIsSummarising(false)
      }
    }
  }, [riskId, summariseRecord, refetchConversations, setCurrentConversationId])

  const onCancel = React.useCallback(async () => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    setIsRunning(false)
    setMessages((prev) => {
      const last = prev[prev.length - 1]
      return last?.role === 'user' ? prev.slice(0, -1) : prev
    })
  }, [])

  const onNew = React.useCallback(
    async (message: AppendMessage) => {
      const typedText = message.content
        .filter((content) => content.type === 'text')
        .map((content) => (content as { type: 'text'; text: string }).text)
        .join('\n')
        .trim()

      const completedAttachments = message.attachments ?? []

      if (!typedText && completedAttachments.length === 0) {
        return
      }

      // Build full message string (text + attachment URLs)
      const messageText = [
        typedText,
        ...completedAttachments.map(
          (att) => (att.content[0] as { type: 'text'; text: string } | undefined)?.text ?? att.name
        )
      ]
        .filter(Boolean)
        .join('\n')

      const userMessage: ChatMessage = {
        id: uuid(),
        role: 'user',
        content: typedText,
        attachments:
          completedAttachments.length > 0
            ? completedAttachments.map((att) => ({
                id: att.id,
                name: att.name,
                type: att.type,
                url: (att.content[0] as { type: 'text'; text: string } | undefined)?.text
              }))
            : undefined
      }

      setMessages((prev) => [...prev, userMessage])
      setIsRunning(true)

      const abortController = new AbortController()
      abortControllerRef.current = abortController

      try {
        const { data } = await sendAiMessage({
          variables: {
            input: {
              ...getContextVariables(context),
              message: messageText,
              conversationId: currentConversationId
            }
          },
          context: { fetchOptions: { signal: abortController.signal } }
        })

        abortControllerRef.current = null

        if (!isMounted.current) {
          return
        }

        const result = data?.sendAiMessage

        // If a new conversation was created by the backend, store it and refresh the list
        if (result?.conversationId && !currentConversationId) {
          setCurrentConversationId(result.conversationId)
          refetchConversations()
        }

        const assistantMessageId = uuid()
        const assistantMessage: ChatMessage = {
          id: assistantMessageId,
          role: 'assistant',
          content: result?.response ?? ''
        }

        setMessageMetadata((prev) => ({
          records: result?.records?.length ? { ...prev.records, [assistantMessageId]: result.records } : prev.records,
          riskSuggestions: result?.riskSuggestions?.length
            ? { ...prev.riskSuggestions, [assistantMessageId]: result.riskSuggestions }
            : prev.riskSuggestions
        }))

        setMessages((prev) => [...prev, assistantMessage])
        setIsRunning(false)
      } catch (err) {
        abortControllerRef.current = null

        if (!isMounted.current) {
          return
        }

        // If cancelled by user — silently discard, state already cleaned up in onCancel
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }

        setMessages((prev) => [
          ...prev,
          {
            id: uuid(),
            role: 'assistant',
            content: 'Failed to get a response. Please try again.'
          }
        ])
        setIsRunning(false)
      }
    },
    [currentConversationId, context, sendAiMessage, refetchConversations, setCurrentConversationId]
  )

  const runtime = useExternalStoreRuntime({
    isRunning,
    messages,
    convertMessage,
    onNew,
    onCancel,
    adapters: { attachments: attachmentAdapter }
  })

  const chatActions = React.useMemo(
    () => ({ summarise: riskId ? summarise : null, isSummarising, isLoadingMessages }),
    [riskId, summarise, isSummarising, isLoadingMessages]
  )

  return (
    <ChatActionsContext.Provider value={chatActions}>
      <MessageMetadataContext.Provider value={messageMetadata}>
        <AssistantRuntimeProvider runtime={runtime}>{children}</AssistantRuntimeProvider>
      </MessageMetadataContext.Provider>
    </ChatActionsContext.Provider>
  )
}

export default ChatRuntimeProvider
